// 261010 Epic 2 / P4b-2 — MỘT thẻ location của tab Bối cảnh (theo mẫu `shot-01`): ảnh chuẩn · lưới ảnh theo hướng · "Hướng các shot đang cần".
// Thẻ TỰ nạp `listLocationViews(locationId)` (nguồn trạng thái ảnh chuẩn / hướng tươi hơn `location-needs`) và TỰ poll khi có view GENERATING
// (job `FILM_LOCATION_VIEW` không mang `planId` nên `workflow-status` không thấy — xem locationViewPoller.ts). Logic quyết định nằm ở locationAssetsPolicy.ts (thuần, có test).
// Sau MỌI thao tác ghi (thành công hay lỗi) gọi `onActionDone` để trang tải lại `workflow-status` / chuông, như P2b.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Dropdown, Image, Modal, Spin } from 'antd';
import { filmApi } from '../../services/api/film';
import { takeWorkflowHandoff } from '../../services/api/workflowReadHandoff';
import { AK } from './workflowAssetsPreload';
import { useAliveRef } from '../../hooks/useAliveRef';
import type { LocationNeedShot, LocationNeedsLocation, LocationView, LocationViewsResponse } from '../../types/filmLocationAssets';
import { workflowActionError, type ActionError } from './workflowActionPolicy';
import { apiErrorText } from './apiErrorText';
import { createLocationViewPoller, type LocationViewPoller } from './locationViewPoller';
import {
  approvedShotCount, canonicalChip, generateConfirm, locationActions, prefillFromShot, shotAssignment, shotLine, videosUsing,
  viewActions, viewChip, viewNote, type LocAction, type LocActionKey,
} from './locationAssetsPolicy';
import LocationViewFormModal, { type FormMode } from './LocationViewFormModal';
import LocationViewCompareDrawer from './LocationViewCompareDrawer';
import { needsConfirm } from './workflowConfirmPolicy';
import { newRequestId } from './workflowPrimaryPolicy';
import WfIcon from './WfIcon'; // UI-assettabs (panel)
import { IconChip } from './StatusIcon';
import { viewStatusViewOf } from './assetCardsUi';
import './assetCards.css';

interface Props {
  location: LocationNeedsLocation;
  onActionDone: () => void;
  /** Khung Sky: FilmAssetCard đã hiện ảnh chuẩn + gen — thẻ này chỉ khối views / shot. */
  viewsOnly?: boolean;
  /**
   * UI-assettabs — bảng bối cảnh MỘT khối đầy bề rộng: đầu (tên + chip + nút nhóm hướng + ⋯) · thân hai cột (ảnh chuẩn lớn | ảnh theo hướng) · chân (mô tả / bố cục / khu vực + shot).
   * Cha (WorkflowLocationPanel) truyền các mảnh gắn với CHÍNH bối cảnh này; thẻ lo dữ liệu hướng; ảnh chuẩn tự duyệt khi lưu.
   */
  panel?: PanelSlots;
  refreshKey?: string;
  planId?: string;
  /** 261029 P1 B2d: báo cho cha bản views thẻ ĐÃ nạp (null khi đang nạp lại) để cha chỉ bỏ lượt nạp thừa khi hai nguồn tương đương. */
  onViewsLoaded?: (views: LocationView[] | null) => void;
}

export interface PanelSlots {
  autoRunning?: boolean;
  title: React.ReactNode;
  /** Nút nhóm hướng + ⋯ (đầu panel, bên phải). */
  actions: React.ReactNode;
  /** Nút tạo / tải ảnh chuẩn đè lên góc ảnh. */
  canonTools: React.ReactNode;
  /** Chân panel: mô tả, bố cục, khu vực, đề xuất gom hướng. */
  footer: React.ReactNode;
}

const Btn: React.FC<{ a: LocAction; busy: boolean; onClick: () => void; primary?: boolean }> = ({ a, busy, onClick, primary }) => (
  <button type="button" className={`wf-btn${primary && a.enabled ? ' primary' : ''}`} disabled={!a.enabled || busy} title={a.reason} onClick={onClick} data-action={a.key}>{a.label}</button>
);

export const ShotRow: React.FC<{ shot: LocationNeedShot; views: LocationView[] | null; onAdd?: (s: LocationNeedShot) => void; onOpenView?: (viewKey: string) => void }> = ({ shot, views, onAdd, onOpenView }) => {
  const as = shotAssignment(shot, views);
  const meta = [shot.shotSize, shot.axis].filter(Boolean).join(' · ');
  const viewOfShot = shot.storyboardViewKey ? views?.find((v) => v.directionKey === shot.storyboardViewKey) : undefined;
  return (
    <li className={`wf-shot${as.needsView && onAdd ? ' need' : ''}`} data-testid="wf-shot-row">
      <div className="wf-shot-l"><b>{shotLine(shot)}</b>{meta ? <span className="muted">{meta}</span> : null}</div>
      <div className="wf-shot-m">Vị trí máy: {shot.cameraPosition ? <q>{shot.cameraPosition}</q> : <span className="faint">(shot chưa ghi vị trí máy)</span>}</div>
      <div className="wf-shot-r">
        <span className={as.kind === 'ORPHAN' ? 'wf-view-note bad' : ''}>{as.text}</span>
        {as.chip ? <span className={`wf-chip ${as.chip.tone}`}>{as.chip.label}</span> : null}
        {as.needsView && onAdd ? <button type="button" className="wf-btn primary" onClick={() => onAdd(shot)}>Thêm hướng này</button> : null}
        {viewOfShot && onOpenView ? <button type="button" className="wf-btn" onClick={() => onOpenView(viewOfShot.id)}>Xem hướng</button> : null}
      </div>
    </li>
  );
};

const LocationAssetCard: React.FC<Props> = ({ location, onActionDone, viewsOnly, panel, refreshKey, planId, onViewsLoaded }) => {
  const locationId = location.locationId;
  const alive = useAliveRef();
  const [data, setData] = useState<LocationViewsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pollError, setPollError] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [actionError, setActionError] = useState<ActionError | null>(null);
  const [form, setForm] = useState<FormMode | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareId, setCompareId] = useState<string | null>(null);
  const poller = useRef<LocationViewPoller | null>(null);
  const doneRef = useRef(onActionDone);
  doneRef.current = onActionDone;
  const loadedRef = useRef(onViewsLoaded);
  loadedRef.current = onViewsLoaded;
  useEffect(() => { loadedRef.current?.(data ? data.views : null); }, [data]);

  const apply = useCallback((r: LocationViewsResponse) => {
    if (!alive.current) return;
    setData(r);
    poller.current?.sync(r.views);
    setPollError(!!poller.current?.isErrored()); // poll đang chết vì lỗi mạng thì dòng "Không cập nhật được trạng thái" phải còn tới khi bấm Tải lại — sau thao tác ghi cũng không xoá
  }, [alive]);

  // P3 F2: lần nạp ĐẦU của thẻ cho bối cảnh này lấy từ chỗ giao một lần (đọc sớm trước `row`); nạp lại (refreshKey, sau ghi, poll) luôn gọi mới.
  const tookEarly = useRef<string | null>(null);
  const initialLoad = useCallback(async () => {
    setLoadError(null);
    const early = tookEarly.current !== locationId; tookEarly.current = locationId;
    try { apply(await (early ? takeWorkflowHandoff(AK.locList(locationId), () => filmApi.listLocationViews(locationId)) : filmApi.listLocationViews(locationId))); } catch (e) { if (alive.current) setLoadError(apiErrorText(e, 'Tải ảnh theo hướng')); }
  }, [locationId, apply, alive]);

  useEffect(() => {
    const p = createLocationViewPoller({
      fetchViews: () => filmApi.listLocationViews(locationId),
      onData: (r) => { setData(r); setPollError(false); },
      onFinished: () => doneRef.current(), // một hướng vừa tạo xong ⇒ trang tải lại workflow-status / chuông
      onError: () => setPollError(true),
    });
    poller.current = p;
    setData(null); setPollError(false); setBusyKey(null); setActionError(null);
    void initialLoad();
    return () => { p.stop(); poller.current = null; };
  }, [locationId, initialLoad, refreshKey]);

  /** Chạy một thao tác ghi. `fn` trả danh sách đầy đủ (create / defaults / PATCH) thì dùng thẳng; không thì nạp lại. Thành công hay lỗi đều nạp lại + `onActionDone`. */
  const run = useCallback(async (key: string, what: string, fn: () => Promise<LocationViewsResponse | unknown>, inlineError = false): Promise<ActionError | null> => {
    setBusyKey(key); setActionError(null);
    let err: ActionError | null = null;
    let list: LocationViewsResponse | null = null;
    try {
      const r: any = await fn();
      if (r && Array.isArray(r.views) && r.canonical) list = r as LocationViewsResponse;
    } catch (e) { err = workflowActionError(e, what); }
    try { list = list ?? await filmApi.listLocationViews(locationId); } catch { /* giữ dữ liệu cũ */ }
    if (alive.current) {
      if (list) apply(list);
      if (err && !inlineError) setActionError(err); // lỗi của hộp Thêm / Sửa hiện ngay trong hộp, không lặp ở thẻ
      setBusyKey(null);
    }
    doneRef.current();
    return err;
  }, [locationId, apply, alive]);

  const views = data?.views ?? null;
  const canonical = data ? { url: data.canonical.url, state: data.canonical.approval.state } : location.canonical;
  const generating = !!views?.some((v) => v.status === 'GENERATING');
  const busy = busyKey !== null;

  const onViewAction = (key: LocActionKey, v: LocationView) => {
    if (key === 'VIEW_GENERATE') {
      if (planId && v.directionKey.startsWith('p-')) {
        void run(`gen:${v.id}`, 'Tạo ảnh hướng', () => filmApi.runWorkflowLocationView(planId, v.id, newRequestId(), !!v.imageUrl));
        return;
      }
      // U9: tạo ảnh hướng là chạy bước tài sản (ảnh, không phải clip video) ⇒ chạy ngay.
      if (needsConfirm('ASSETS', 'RUN')) { const c = generateConfirm(v, location.name); Modal.confirm({ title: c.title, content: c.body, okText: c.okText, cancelText: c.cancelText, onOk: () => { void run(`gen:${v.id}`, 'Tạo ảnh hướng', () => filmApi.generateView(v.id)); } }); }
      else void run(`gen:${v.id}`, 'Tạo ảnh hướng', () => filmApi.generateView(v.id));
    } else if (key === 'VIEW_APPROVE') void run(`apr:${v.id}`, 'Duyệt ảnh hướng', () => filmApi.approveView(v.id));
    else if (key === 'VIEW_UNAPPROVE') void run(`unapr:${v.id}`, 'Bỏ duyệt ảnh hướng', () => filmApi.unapproveView(v.id));
    else if (key === 'VIEW_EDIT_TEXT') setForm({ kind: 'edit', view: v });
  };
  const openAdd = (initial = { label: '', directionKey: '', directionText: '' }, hint?: string) =>
    setForm({ kind: 'add', initial, existingKeys: (views ?? []).map((v) => v.directionKey), hint });
  const openCompare = (viewId: string) => { setCompareId(viewId); setCompareOpen(true); };

  const cc = canonicalChip(canonical);
  const locActs = views ? locationActions(views) : [];
  const approvedShots = approvedShotCount(location.shots, views);

  if (panel) {
    const ccTone = cc.tone === 'ok' ? 'check' : cc.tone === 'bad' ? 'warning' : cc.tone === 'warn' ? 'eye' : 'clock';
    const shotsTotal = location.shots.length;
    return (
      <section className="ac-lp" aria-label={`Bối cảnh ${location.name}`} data-testid="ac-loc-panel" data-location-id={locationId} aria-busy={busy || generating}>
        <header className="ac-lp-h">
          {panel.title}
          <IconChip label={`Ảnh chuẩn: ${cc.label}`} icon={ccTone} tone={cc.tone} />
          {shotsTotal > 0 ? <IconChip label={`${approvedShots}/${shotsTotal} shot đã có ảnh hướng duyệt · ${videosUsing(location.shots)} video dùng`} icon="picture" tone={approvedShots === shotsTotal ? 'ok' : 'warn'} value={`${approvedShots}/${shotsTotal}`} /> : null}
          <span className="sp" />
          {panel.actions}
        </header>
        <div className="ac-lp-body">
          <div className="ac-lp-canon" data-testid="wf-canon-slim">
            <div className="ac-lp-img">
              {canonical.url
                ? <Image src={canonical.url} alt={`Ảnh chuẩn ${location.name}`} width="100%" height="100%" style={{ objectFit: 'contain' }} />
                : <div className="empty">Chưa có ảnh chuẩn</div>}
              <div className="ac-lp-ov">{panel.canonTools}</div>
            </div>
            <div className="ac-lp-cap"><b>Ảnh chuẩn</b><span className="faint">nguồn để vẽ mọi ảnh theo hướng</span></div>
          </div>

          <div className="ac-lp-views">
            <div className="ac-lp-vh"><b>Ảnh theo hướng</b>{data ? <span className="n">{data.views.length}</span> : null}
              <span className="sp" />
              <Dropdown trigger={['click']} menu={{ items: [
                { key: 'custom', label: 'Thêm hướng riêng', disabled: busy, onClick: () => openAdd() },
                { key: 'defaults', label: 'Tạo 2 hướng mặc định', disabled: busy, onClick: () => void run('defaults', 'Tạo hướng mặc định', () => filmApi.createDefaultViews(locationId)) },
              ] }}><button type="button" className="wf-btn" aria-label="Thêm hướng thủ công"><WfIcon name="ellipsis" /></button></Dropdown>
            </div>
            {!data && !loadError ? <div className="ac-lp-load"><Spin size="small" /> <span className="muted">Đang tải ảnh theo hướng…</span></div> : null}
            {loadError ? <div className="wf-alert ERROR" role="alert">{loadError} <button type="button" className="wf-btn" onClick={() => void initialLoad()}>Tải lại</button></div> : null}
            {actionError ? <div className="wf-alert ERROR" role="alert" data-testid="wf-action-error">{actionError.text} <button type="button" className="wf-btn" onClick={() => { setActionError(null); void poller.current?.reload(); }}>Tải lại</button></div> : null}
            {pollError ? <div className="wf-alert WARN" role="status" data-testid="wf-poll-error">Không cập nhật được trạng thái — <button type="button" className="wf-link" onClick={() => { void poller.current?.reload(); }}>Tải lại</button></div> : null}
            {data && data.views.length === 0 ? (
              <div className="wf-vempty">
                <div className="muted" style={{ margin: 0 }}>{panel.autoRunning ? <><Spin size="small" /> Máy đang gom và tạo ảnh hướng</> : 'Máy sẽ tự gom và tạo ảnh hướng'}</div>
              </div>
            ) : null}
            {data && data.views.length > 0 ? (
              <div className="ac-vgrid">
                {data.views.map((v) => {
                  const st = viewStatusViewOf(v.status); const note = viewNote(v); const acts = viewActions(v, canonical);
                  const gen = acts.find((a) => a.key === 'VIEW_GENERATE');
                  const missing = !v.imageUrl && v.status !== 'GENERATING';
                  const quickGen = missing && !!gen?.enabled && !busy;
                  return (
                    <div key={v.id} className={`ac-vc ${v.status}`} data-testid="wf-view-cell" data-direction-key={v.directionKey}>
                      <button type="button" className={`ac-vthumb${v.imageUrl ? '' : ' empty'}${v.status === 'STALE' ? ' old' : ''}`}
                        aria-label={quickGen ? `Tạo ảnh hướng ${v.label}` : `Xem so sánh hướng ${v.label}`}
                        onClick={() => (quickGen ? onViewAction('VIEW_GENERATE', v) : openCompare(v.id))}>
                        {v.imageUrl ? <img src={v.imageUrl} alt={`Ảnh hướng ${v.label}`} loading="lazy" /> : <WfIcon name={v.status === 'GENERATING' ? 'loading' : 'plus'} />}
                        <span className="st"><IconChip label={`${st.label}${note ? ` — ${note}` : ''}`} icon={st.icon} tone={st.tone} focusable={false} /></span>
                      </button>
                      <div className="ac-vl" title={v.directionText}><b>{v.label}</b></div>
                      <div className="ac-vacts"><Dropdown trigger={['click']} menu={{ items: acts.map((a) => ({
                        key: a.key, label: a.label, disabled: !a.enabled || busy, onClick: () => onViewAction(a.key, v),
                      })) }}><button type="button" className="wf-btn" aria-label={`Thao tác hướng ${v.label}`}><WfIcon name="ellipsis" /></button></Dropdown></div>
                    </div>);
                })}
              </div>
            ) : null}
          </div>
        </div>

        <div className="ac-lp-foot">
          {panel.footer}
          <details className="ac-fold" data-testid="wf-needs">
            <summary>Hướng các shot đang cần ({location.shots.length} shot)</summary>
            <p className="faint" style={{ fontSize: 12.5, margin: '6px 0' }}>Máy gom hướng theo vị trí máy của từng shot rồi Storyboard gán ảnh. Có thể thêm hoặc sửa hướng trong menu ⋯.</p>
            {location.shots.length === 0 ? <p className="muted">Chưa có shot nào ở bối cảnh này.</p> : (
              <ul className="wf-shots">
                {location.shots.map((s) => (
                  <ShotRow key={s.shotId} shot={s} views={views} onOpenView={openCompare}
                    onAdd={(sh) => openAdd(prefillFromShot(sh), `Từ ${shotLine(sh)}. Mô tả điền sẵn từ vị trí máy và nền — sửa lại rồi đặt nhãn và mã hướng.`)} />))}
              </ul>)}
          </details>
        </div>

        <LocationViewFormModal mode={form} onClose={() => setForm(null)}
          onSubmitAdd={(v) => run('add', 'Thêm hướng', () => filmApi.createView(locationId, { directionKey: v.directionKey, label: v.label, directionText: v.directionText }), true)}
          onSubmitEdit={(view, patch) => run(`edit:${view.id}`, 'Sửa hướng', () => filmApi.updateView(view.id, patch), true)} />
        <LocationViewCompareDrawer open={compareOpen} onClose={() => setCompareOpen(false)} locationName={location.name} canonical={canonical} views={data?.views ?? []}
          activeId={compareId} onSelect={setCompareId} busy={busy} actionsOf={(v) => viewActions(v, canonical)} onAction={onViewAction} />
      </section>
    );
  }

  return (
    <section className="wf-loc" aria-label={`Bối cảnh ${location.name}`} data-testid="wf-loc-card" aria-busy={busy || generating}>
      {viewsOnly ? null : (
      <header className="wf-loc-h">
        <h3>{location.name}</h3>
        <span className={`wf-chip ${approvedShots === location.shots.length && location.shots.length > 0 ? 'ok' : 'warn'}`}>{approvedShots}/{location.shots.length} shot đã có ảnh hướng duyệt</span>
        <span className="wf-loc-n">{videosUsing(location.shots)} video dùng</span>
      </header>
      )}

      {!data && !loadError ? <div style={{ padding: 16 }}><Spin size="small" /> <span className="muted">Đang tải ảnh theo hướng…</span></div> : null}
      {loadError ? <div className="wf-alert ERROR" role="alert">{loadError} <button type="button" className="wf-btn" onClick={() => void initialLoad()}>Tải lại</button></div> : null}

      {data ? (
        <div className="wf-loc-body">
          {viewsOnly ? (
            <div className="wf-canon" data-testid="wf-canon-slim">
              <div className="wf-cmp-h"><b>Ảnh chuẩn</b><span className={`wf-chip ${cc.tone}`}>{cc.label}</span>
              </div>
            </div>
          ) : <div className="wf-canon">
            <div className="wf-cmp-h"><b>Ảnh chuẩn</b><span className={`wf-chip ${cc.tone}`}>{cc.label}</span></div>
            {canonical.url ? <img className="wf-img" src={canonical.url} alt={`Ảnh chuẩn ${location.name}`} loading="lazy" /> : <div className="wf-vthumb empty wf-canon-empty">Chưa có ảnh chuẩn</div>}
            <p className="faint" style={{ fontSize: 12.5, margin: '6px 0' }}>Là nguồn để vẽ mọi ảnh theo hướng.</p>
          </div>}

          <div className="wf-views">
            <div className="wf-cmp-h"><b>Ảnh theo hướng</b><span className="wf-loc-n">{data.views.length}</span>
              {data.views.length > 0 && locActs.filter((a) => a.key === 'VIEW_ADD').map((a) => <button key={a.key} type="button" className="wf-btn" style={{ marginLeft: 'auto' }} disabled={busy} onClick={() => openAdd()}>+ {a.label}</button>)}
            </div>
            {actionError ? <div className="wf-alert ERROR" role="alert" data-testid="wf-action-error">{actionError.text} <button type="button" className="wf-btn" onClick={() => { setActionError(null); void poller.current?.reload(); }}>Tải lại</button></div> : null}
            {pollError ? <div className="wf-alert WARN" role="status" data-testid="wf-poll-error">Không cập nhật được trạng thái — <button type="button" className="wf-link" onClick={() => { void poller.current?.reload(); }}>Tải lại</button></div> : null}

            {data.views.length === 0 ? (
              <div className="wf-vempty">
                <p className="muted" style={{ margin: 0 }}>Chưa có hướng nào.</p>
                <p className="muted" style={{ margin: '2px 0 8px' }}>Mặc định có 2 hướng: Nhìn vào và Đứng tại chỗ nhìn ra. Bước này chỉ tạo hướng, chưa tạo ảnh.</p>
                <div className="wf-actions" style={{ justifyContent: 'center' }}>
                  {locActs.filter((a) => a.key === 'VIEWS_DEFAULTS').map((a) => <Btn key={a.key} a={a} busy={busy} primary onClick={() => void run('defaults', 'Tạo hướng mặc định', () => filmApi.createDefaultViews(locationId))} />)}
                  <button type="button" className="wf-btn" disabled={busy} onClick={() => openAdd()}>+ Thêm hướng riêng</button>
                </div>
              </div>
            ) : (
              <div className="wf-vgrid">
                {data.views.map((v) => {
                  const chip = viewChip(v); const note = viewNote(v); const acts = viewActions(v, canonical);
                  return (
                    <div key={v.id} className={`wf-vcell ${v.status}`} data-testid="wf-view-cell">
                      <button type="button" className={`wf-vthumb${v.imageUrl ? '' : ' empty'}${v.status === 'STALE' ? ' old' : ''}`} onClick={() => openCompare(v.id)} aria-label={`Xem so sánh hướng ${v.label}`}>
                        {v.imageUrl ? <img src={v.imageUrl} alt={`Ảnh hướng ${v.label}`} loading="lazy" /> : <span>{v.status === 'GENERATING' ? 'Đang tạo ảnh…' : 'Chưa có ảnh'}</span>}
                        <span className="wf-vkey mono">{v.directionKey}</span>
                      </button>
                      <div className="wf-vinfo">
                        <b>{v.label}</b>
                        <span className={`wf-chip ${chip.tone}`}>{chip.label}</span>
                        {note ? <span className={`wf-view-note${v.status === 'ERROR' ? ' bad' : ''}`}>{note}</span> : null}
                        <p className="wf-vdesc" title={v.directionText}>{v.directionText}</p>
                        <div className="wf-actions">{acts.map((a) => <Btn key={a.key} a={a} busy={busy} primary={a.key === 'VIEW_GENERATE'} onClick={() => onViewAction(a.key, v)} />)}</div>
                        {v.status === 'GENERATING' ? null : acts.filter((a) => a.key === 'VIEW_GENERATE' && !a.enabled && a.reason).map((a) => <span key={a.key} className="faint" style={{ fontSize: 12 }}>{a.reason}</span>)}
                      </div>
                    </div>);
                })}
              </div>
            )}
          </div>

          <div className="wf-needs">
            <div className="wf-cmp-h"><b>Hướng các shot đang cần</b><span className="wf-loc-n">{location.shots.length} shot</span></div>
            <p className="faint" style={{ fontSize: 12.5, margin: '0 0 6px' }}>Vị trí máy lấy nguyên văn từ từng shot (bước Quay phim). Hệ thống không tự đoán hướng — người đặt hướng rồi storyboard gán.</p>
            {location.shots.length === 0 ? <p className="muted">Chưa có shot nào ở bối cảnh này.</p> : (
              <ul className="wf-shots">
                {location.shots.map((s) => (
                  <ShotRow key={s.shotId} shot={s} views={views} onOpenView={openCompare}
                    onAdd={(sh) => openAdd(prefillFromShot(sh), `Từ ${shotLine(sh)}. Mô tả điền sẵn từ vị trí máy và nền — sửa lại rồi đặt nhãn và mã hướng.`)} />))}
              </ul>)}
          </div>
        </div>
      ) : null}

      <LocationViewFormModal mode={form} onClose={() => setForm(null)}
        onSubmitAdd={(v) => run('add', 'Thêm hướng', () => filmApi.createView(locationId, { directionKey: v.directionKey, label: v.label, directionText: v.directionText }), true)}
        onSubmitEdit={(view, patch) => run(`edit:${view.id}`, 'Sửa hướng', () => filmApi.updateView(view.id, patch), true)} />
      <LocationViewCompareDrawer open={compareOpen} onClose={() => setCompareOpen(false)} locationName={location.name} canonical={canonical} views={data?.views ?? []}
        activeId={compareId} onSelect={setCompareId} busy={busy} actionsOf={(v) => viewActions(v, canonical)} onAction={onViewAction} />
    </section>
  );
};

export default LocationAssetCard;
